// Shared muscle utilities — target muscle colors, stats calculator with secondary credit, balance
// Maps the 14 ExerciseDB target muscles to colors
import { translateMuscle } from './muscleTranslations';

// ── Display name overrides ──────────────────────────────────────────────────
// Internal data keys stay unchanged so old workouts never break.
// Only the text shown to users is different.
const MUSCLE_DISPLAY_NAMES = {
  'pectorals':  'Chest',
  'chest':      'Chest',   // alias — some paths produce 'chest'
  'upper back': 'Back',
  'back':       'Back',    // alias — split-bar paths produce 'back'
};

/**
 * Convert an internal muscle key to a human-friendly display name.
 * - EN: uses MUSCLE_DISPLAY_NAMES override for nicer labels (pectorals -> Chest),
 *   falls back to Title Case.
 * - PT: delegates to translateMuscle (reads muscle_translations.json).
 */
export function displayMuscleName(muscle, lang = 'en') {
  if (!muscle) return '';
  if (lang === 'pt') {
    return translateMuscle(muscle, 'pt');
  }
  const key = muscle.toLowerCase();
  return MUSCLE_DISPLAY_NAMES[key] || (key.charAt(0).toUpperCase() + key.slice(1));
}

export const TARGET_MUSCLE_COLORS = {
  'pectorals':  'bg-red-500/15 text-red-400 border-red-500/25',
  'lats':       'bg-blue-500/15 text-blue-400 border-blue-500/25',
  'upper back': 'bg-sky-500/15 text-sky-400 border-sky-500/25',
  'traps':      'bg-indigo-500/15 text-indigo-400 border-indigo-500/25',
  'delts':      'bg-amber-500/15 text-amber-400 border-amber-500/25',
  'biceps':     'bg-purple-500/15 text-purple-400 border-purple-500/25',
  'triceps':    'bg-violet-500/15 text-violet-400 border-violet-500/25',
  'abs':        'bg-orange-500/15 text-orange-400 border-orange-500/25',
  'glutes':     'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  'quads':      'bg-green-500/15 text-green-400 border-green-500/25',
  'hamstrings': 'bg-teal-500/15 text-teal-400 border-teal-500/25',
  'calves':     'bg-cyan-500/15 text-cyan-400 border-cyan-500/25',
  'abductors':  'bg-lime-500/15 text-lime-400 border-lime-500/25',
  'adductors':  'bg-pink-500/15 text-pink-400 border-pink-500/25',
  // Fallback aliases
  'forearms':   'bg-indigo-500/15 text-indigo-400 border-indigo-500/25',
  'core':       'bg-orange-500/15 text-orange-400 border-orange-500/25',
  'shoulders':  'bg-amber-500/15 text-amber-400 border-amber-500/25',
  'rhomboids':  'bg-sky-500/15 text-sky-400 border-sky-500/25',
  'lower back': 'bg-blue-400/15 text-blue-300 border-blue-400/25',
  'rear deltoids': 'bg-amber-400/15 text-amber-300 border-amber-400/25',
  'quadriceps': 'bg-green-500/15 text-green-400 border-green-500/25',
};

// Which muscles are "upper body" (push or pull) vs "lower body"
const UPPER_PUSH = ['pectorals', 'delts', 'triceps', 'shoulders'];
const UPPER_PULL = ['lats', 'upper back', 'traps', 'biceps', 'rhomboids', 'rear deltoids', 'forearms'];
const LOWER = ['glutes', 'quads', 'hamstrings', 'calves', 'abductors', 'adductors', 'quadriceps'];
const CORE = ['abs', 'obliques', 'serratus anterior', 'core', 'lower back'];

// Canonical 5-group taxonomy for display ONLY. Data layer keeps raw target
// muscles as its own keys; this map is a lookup for UIs that want to show
// "Chest / Back / Arms / Legs / Core" instead of the 20+ raw muscles.
export const MUSCLE_GROUPS = ['Chest', 'Back', 'Arms', 'Legs', 'Core'];
export const TARGET_TO_GROUP = {
  pectorals: 'Chest',
  lats: 'Back', 'upper back': 'Back', rhomboids: 'Back', traps: 'Back',
  'lower back': 'Back', 'levator scapulae': 'Back',
  delts: 'Arms', 'rear deltoids': 'Arms', biceps: 'Arms', triceps: 'Arms', forearms: 'Arms',
  quads: 'Legs', hamstrings: 'Legs', glutes: 'Legs',
  adductors: 'Legs', abductors: 'Legs', calves: 'Legs',
  abs: 'Core', obliques: 'Core', 'serratus anterior': 'Core', core: 'Core',
};
export const MUSCLE_GROUP_COLORS = {
  Chest: '#ef4444', Back: '#3b82f6', Arms: '#8b5cf6', Legs: '#22c55e', Core: '#f97316',
};

// Normalize raw DB muscle strings to canonical target-muscle keys.
// Only synonyms and bodyPart fallbacks — no target-to-target merges. Lats,
// rhomboids, rear deltoids etc. stay as their own keys; the body-map paint
// layer sums them visually where one SVG region covers several muscles.
function normalizeMuscleName(name) {
  const n = (name || '').toLowerCase().trim();
  const map = {
    'quadriceps': 'quads',
    'shoulders': 'delts',
    'spine': 'lower back',
  };
  return map[n] || n;
}

/**
 * Calculate work sets per TARGET MUSCLE with secondary credit (0.5x)
 *
 * IMPORTANT: Routines store a frozen snapshot of targetMuscles/secondaryMuscles
 * on each exercise when it's added. That snapshot can be stale or wrong (old
 * imports, historical bugs). When `exerciseMuscleMap` is provided, we use it as
 * the source of truth — overriding the stored values with whatever the live
 * exercises collection currently says. Custom exercises (not in the map) fall
 * back to their stored values, because there's nowhere else to look them up.
 *
 * Workout session history should NOT pass a muscle map — historical workouts
 * must remain frozen so that re-tagging an exercise doesn't retroactively
 * change completed sessions.
 *
 * @param {Array} days - routine days array
 * @param {boolean} includeSecondary - whether to include secondary muscles at 0.5x
 * @param {Object} [exerciseMuscleMap] - optional { [exerciseName]: { targetMuscles, secondaryMuscles } }
 *                                        live override from the exercises collection
 * @returns {{ total, perDay }}
 */
export function getTargetMuscleStats(days, includeSecondary = true, exerciseMuscleMap = null) {
  const total = {};
  const perDay = (days || []).map(day => {
    const dayStats = {};
    (day.exercises || []).forEach(ex => {
      const workSets = (ex.sets || []).filter(s => s.set_type === 'work' || s.set_type === 'backoff').length;
      if (workSets <= 0) return;

      // Live override: if we have a muscle map and this exercise is in it,
      // use the live values instead of the (possibly stale) stored snapshot.
      const live = exerciseMuscleMap && exerciseMuscleMap[ex.name];
      const targets =
        (live && live.targetMuscles && live.targetMuscles.length)
          ? live.targetMuscles
          : (ex.targetMuscles || []);
      const secondaries =
        (live && live.secondaryMuscles && live.secondaryMuscles.length)
          ? live.secondaryMuscles
          : (ex.secondaryMuscles || []);

      // Primary target muscles (1x credit) — dedup so ['quads','quadriceps'] counts once
      const primaryKeys = new Set();
      targets.forEach(m => {
        const nm = normalizeMuscleName(m);
        if (nm) primaryKeys.add(nm);
      });
      primaryKeys.forEach(nm => {
        dayStats[nm] = (dayStats[nm] || 0) + workSets;
        total[nm] = (total[nm] || 0) + workSets;
      });

      // Secondary muscles (0.5x credit) — dedup so ['lats','rhomboids','upper back'] only
      // contributes once to 'upper back'. Also skip any key already credited as primary.
      if (includeSecondary) {
        const secondaryKeys = new Set();
        secondaries.forEach(m => {
          const nm = normalizeMuscleName(m);
          if (nm && !primaryKeys.has(nm)) secondaryKeys.add(nm);
        });
        secondaryKeys.forEach(nm => {
          dayStats[nm] = (dayStats[nm] || 0) + (workSets * 0.5);
          total[nm] = (total[nm] || 0) + (workSets * 0.5);
        });
      }
    });
    return dayStats;
  });

  // Round all values to 1 decimal
  Object.keys(total).forEach(k => { total[k] = Math.round(total[k] * 10) / 10; });
  perDay.forEach(d => Object.keys(d).forEach(k => { d[k] = Math.round(d[k] * 10) / 10; }));

  return { total, perDay };
}

/**
 * Fallback: compute stats from muscle_group when targetMuscles is missing
 */
export function getMuscleGroupStats(days) {
  const total = {};
  const perDay = (days || []).map(day => {
    const dayStats = {};
    (day.exercises || []).forEach(ex => {
      const mg = ex.muscle_group || 'Other';
      const workSets = (ex.sets || []).filter(s => s.set_type === 'work' || s.set_type === 'backoff').length;
      if (workSets > 0) {
        dayStats[mg] = (dayStats[mg] || 0) + workSets;
        total[mg] = (total[mg] || 0) + workSets;
      }
    });
    return dayStats;
  });
  return { total, perDay };
}

/**
 * Calculate upper/lower and push/pull balance
 * Returns { upperSets, lowerSets, coreSets, pushSets, pullSets, upperPct, lowerPct, pushPct, pullPct }
 */
export function getBalanceStats(muscleStats) {
  let pushSets = 0, pullSets = 0, lowerSets = 0, coreSets = 0;

  Object.entries(muscleStats).forEach(([muscle, sets]) => {
    const nm = normalizeMuscleName(muscle);
    if (UPPER_PUSH.includes(nm)) pushSets += sets;
    else if (UPPER_PULL.includes(nm)) pullSets += sets;
    else if (LOWER.includes(nm)) lowerSets += sets;
    else if (CORE.includes(nm)) coreSets += sets;
  });

  const upperSets = pushSets + pullSets;
  const totalUL = upperSets + lowerSets || 1;
  const totalPP = pushSets + pullSets || 1;

  return {
    upperSets: Math.round(upperSets * 10) / 10,
    lowerSets: Math.round(lowerSets * 10) / 10,
    coreSets: Math.round(coreSets * 10) / 10,
    pushSets: Math.round(pushSets * 10) / 10,
    pullSets: Math.round(pullSets * 10) / 10,
    upperPct: Math.round((upperSets / totalUL) * 100),
    lowerPct: Math.round((lowerSets / totalUL) * 100),
    pushPct: Math.round((pushSets / totalPP) * 100),
    pullPct: Math.round((pullSets / totalPP) * 100),
  };
}

/**
 * Get heat color for a muscle based on sets count (for the body map)
 * Returns a hex color from gray → light orange → orange → dark orange → red
 */
export function getMuscleHeatColor(sets) {
  if (!sets || sets <= 0) return '#3a3a3a'; // gray - no work
  if (sets <= 2) return '#6b4c1e';   // very light warm
  if (sets <= 4) return '#b36b00';   // light orange
  if (sets <= 6) return '#d97706';   // orange
  if (sets <= 8) return '#ea580c';   // dark orange
  if (sets <= 10) return '#dc2626';  // red
  if (sets <= 14) return '#b91c1c';  // darker red
  return '#991b1b';                   // deep red - very high volume
}

/**
 * Heat color label text
 */
export function getMuscleHeatLabel(sets) {
  if (!sets || sets <= 0) return 'No work';
  if (sets <= 4) return 'Low';
  if (sets <= 8) return 'Moderate';
  if (sets <= 12) return 'Good';
  return 'High';
}
